import { FormattedMessage } from '@alltrails/shared/react-intl';
import StarRating, { Rating } from '@alltrails/shared/components/sliders/StarRating';
import useLanguageRegionCode from 'hooks/useLanguageRegionCode';
import { FilterConfigStarRating } from 'types/Search';
import * as styles from './styles/styles.module.scss';

export type StarRatingGroupProps = {
  config: FilterConfigStarRating;
  handler: (changes: FilterConfigStarRating) => void;
};

const STAR_RATINGS = [null, 3.5, 4, 4.5];

const generateRatings = (values: number[], formatter: Intl.NumberFormat) =>
  values.map(value => {
    const formattedValue = formatter.format(value);

    return {
      mark: value === null ? <FormattedMessage defaultMessage="0+" /> : formattedValue,
      label:
        value === null ? (
          <FormattedMessage defaultMessage="0+" />
        ) : (
          <FormattedMessage defaultMessage="Over {value}" values={{ value: formattedValue }} />
        ),
      hasStar: value !== null
    };
  });

const StarRatingGroup = ({ config, handler }: StarRatingGroupProps) => {
  const languageRegionCode = useLanguageRegionCode();

  const formatter = new Intl.NumberFormat(languageRegionCode, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 1
  });

  const ratings: Rating[] = generateRatings(STAR_RATINGS, formatter);

  const valueToIndex = (value: FilterConfigStarRating): number => {
    if (value === null) {
      return 0;
    }

    const index = STAR_RATINGS.findIndex(r => String(r) === value);

    return index !== -1 ? index : 0;
  };

  const indexToValue = (index: number) => (index === 0 ? null : String(STAR_RATINGS[index]));

  return (
    <div className={styles.container}>
      <StarRating
        anyRatingLabel={<FormattedMessage defaultMessage="Any" />}
        selectedIndex={valueToIndex(config)}
        title={<FormattedMessage defaultMessage="Rating" />}
        onChange={index => handler(indexToValue(index))}
        ratings={ratings}
      />
    </div>
  );
};

export default StarRatingGroup;
